import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays, MapPin } from "lucide-react";
import Header from "../sections/Header";
import Footer from "../sections/Footer";
import { Reveal } from "../components/Reveal";

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

const parseDay = (d) => {
    if (!d) return null;
    const dt = new Date(`${d}T00:00:00`);
    return Number.isNaN(dt.getTime()) ? null : dt;
};

const dateRange = (e) => {
    const s = parseDay(e.start_date);
    const en = parseDay(e.end_date);
    if (!s) return "Dates TBA";
    const fmt = (dt) => `${MONTHS[dt.getMonth()]} ${dt.getDate()}`;
    if (!en || e.end_date === e.start_date) return `${fmt(s)}, ${s.getFullYear()}`;
    if (s.getMonth() === en.getMonth())
        return `${fmt(s)} – ${en.getDate()}, ${en.getFullYear()}`;
    return `${fmt(s)} – ${fmt(en)}, ${en.getFullYear()}`;
};

export default function EventsPage() {
    const [events, setEvents] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        fetch(`${API}/api/events`)
            .then((r) => (r.ok ? r.json() : []))
            .then(setEvents)
            .catch(() => setEvents([]));
    }, []);

    useEffect(() => {
        const prevTitle = document.title;
        document.title = "Cannabis Industry Events Calendar | Triple O Creative Industries";
        let meta = document.querySelector('meta[name="description"]');
        const prevDesc = meta?.getAttribute("content") || "";
        if (meta)
            meta.setAttribute(
                "content",
                "Upcoming cannabis industry events, trade shows and expos — dates, venues and official links. Exhibiting? Triple O designs, fabricates and installs trade show booths and prints event graphics in Southern California."
            );
        return () => {
            document.title = prevTitle;
            if (meta && prevDesc) meta.setAttribute("content", prevDesc);
        };
    }, []);

    useEffect(() => {
        if (!events || !events.length) return undefined;
        const ld = {
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: events.map((e, i) => ({
                "@type": "ListItem",
                position: i + 1,
                item: {
                    "@type": "Event",
                    name: e.name,
                    startDate: e.start_date || undefined,
                    endDate: e.end_date || undefined,
                    url: e.url || undefined,
                    location: {
                        "@type": "Place",
                        name: e.venue || e.city || "TBA",
                        address: e.city || undefined,
                    },
                    description: e.description || undefined,
                },
            })),
        };
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.dataset.projectSeo = "true";
        script.textContent = JSON.stringify(ld);
        document.head.appendChild(script);
        return () => script.remove();
    }, [events]);

    return (
        <div id="top" data-testid="events-page" className="relative bg-ink text-paper">
            <div className="noise-overlay" aria-hidden="true" />
            <Header />
            <main className="pt-28 lg:pt-36">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-acid">
                            Industry Calendar
                        </p>
                        <h1
                            data-testid="events-title"
                            className="mt-6 font-display text-4xl font-bold uppercase leading-[1.05] tracking-tight text-paper sm:text-6xl lg:text-7xl"
                        >
                            Where the industry
                            <br />
                            <span className="text-acid">shows up.</span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-fog sm:text-lg">
                            The cannabis trade shows, expos and conferences worth your calendar —
                            dates, venues and official links in one place. Walking the floor or
                            taking a booth, this is the map.
                        </p>
                    </Reveal>

                    <div className="mt-16 pb-8">
                        {events === null ? (
                            <p className="font-mono text-xs uppercase tracking-[0.3em] text-paper/50">
                                Loading…
                            </p>
                        ) : events.length === 0 ? (
                            <div
                                data-testid="events-empty"
                                className="border border-dashed border-white/20 p-16 text-center"
                            >
                                <p className="font-display text-xl font-bold uppercase tracking-tight">
                                    New dates drop soon.
                                </p>
                                <p className="mt-3 text-sm text-fog">
                                    The calendar is being loaded — check back shortly.
                                </p>
                            </div>
                        ) : (
                            <ul className="divide-y divide-white/10 border-y border-white/10">
                                {events.map((e, i) => (
                                    <Reveal key={e.id} delay={0.05 * i}>
                                        <li
                                            data-testid={`event-card-${e.id}`}
                                            className="grid grid-cols-1 gap-6 py-10 sm:grid-cols-12 sm:gap-8"
                                        >
                                            <div className="sm:col-span-3">
                                                <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-acid">
                                                    <CalendarDays className="h-4 w-4" aria-hidden="true" />
                                                    {dateRange(e)}
                                                </p>
                                                {(e.venue || e.city) && (
                                                    <p className="mt-3 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-paper/50">
                                                        <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                                                        {[e.venue, e.city].filter(Boolean).join(" · ")}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="sm:col-span-9">
                                                <div className="flex flex-wrap items-center gap-4">
                                                    <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-paper sm:text-3xl">
                                                        {e.name}
                                                    </h2>
                                                    {e.attending && (
                                                        <span className="bg-acid px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-ink">
                                                            Triple O will be there{e.booth ? ` — Booth ${e.booth}` : ""}
                                                        </span>
                                                    )}
                                                </div>
                                                {e.description && (
                                                    <p className="mt-3 max-w-3xl text-sm leading-relaxed text-fog sm:text-base">
                                                        {e.description}
                                                    </p>
                                                )}
                                                <div className="mt-5 flex flex-wrap items-center gap-6">
                                                    {e.url && (
                                                        <a
                                                            href={e.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            data-testid={`event-link-${e.id}`}
                                                            className="group flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-paper/70 transition-colors duration-300 hover:text-acid"
                                                        >
                                                            Official event site
                                                            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                                                        </a>
                                                    )}
                                                    <Link
                                                        to="/#contact"
                                                        className="font-mono text-[11px] uppercase tracking-[0.25em] text-acid/80 transition-colors duration-300 hover:text-acid"
                                                    >
                                                        Exhibiting? We build + print booths →
                                                    </Link>
                                                </div>
                                            </div>
                                        </li>
                                    </Reveal>
                                ))}
                            </ul>
                        )}
                    </div>

                    <Reveal>
                        <div className="my-24 border border-acid/40 bg-panel p-10 text-center sm:p-16">
                            <p className="font-display text-2xl font-bold uppercase tracking-tight text-paper sm:text-4xl">
                                Taking a booth at one of these?
                            </p>
                            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-fog sm:text-base">
                                We design, fabricate and install the build — and if you just need
                                banners, graphics or wraps for show day, the Print Shop has you.
                            </p>
                            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                                <Link
                                    to="/#contact"
                                    data-testid="events-cta-booth"
                                    className="group inline-flex items-center gap-3 bg-acid px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-paper"
                                >
                                    Start a Project
                                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                                </Link>
                                <Link
                                    to="/print"
                                    data-testid="events-cta-print"
                                    className="inline-flex items-center gap-3 border border-white/20 px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:border-acid hover:text-acid"
                                >
                                    The Print Shop
                                </Link>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </main>
            <Footer />
        </div>
    );
}
